import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaEnvelope, FaPhone, FaEdit, FaUser, FaMapMarkerAlt, FaTimes } from 'react-icons/fa';
import API_CONFIG from './config';
import './styles/Users.css';

interface User {
    user_id: number;
    name: string;
    job_role: string;
    email: string;
    phone_number: string;
    site_ids?: string[];
    is_active: boolean;
}

const CARD_WIDTH = 350;
const CARD_HEIGHT = 340;

const Users: React.FC = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState<User[]>([]);
    const [checkingSession, setCheckingSession] = useState(true);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [allSites, setAllSites] = useState<{ site_id: string; site_name: string }[]>([]);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Edit form state
    const [editName, setEditName] = useState('');
    const [editEmail, setEditEmail] = useState('');
    const [editPhone, setEditPhone] = useState('');
    const [editJobRole, setEditJobRole] = useState('');
    const [editSelectedSites, setEditSelectedSites] = useState<string[]>([]);
    const [editIsActive, setEditIsActive] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setCheckingSession(true);

        // validate session first, then request users
        axios.get('/api/session', { withCredentials: true })
            .then(() => {
                return axios.get('/api/all-users', { withCredentials: true, baseURL: API_CONFIG.BASE_URL });
            })
            .then(res => {
                if (cancelled) return;
                console.log('Users:', res.data);
                setUsers(res.data);
            })
            .catch(err => {
                if (cancelled) return;
                if (err?.response?.status === 401) {
                    try { sessionStorage.setItem('sessionExpired', 'true'); } catch {}
                    navigate('/login');
                } else {
                    setUsers([]);
                }
            })
            .finally(() => {
                if (!cancelled) setCheckingSession(false);
            });

        return () => { cancelled = true; };
    }, [navigate]);

    // Fetch sites when needed
    useEffect(() => {
        if (editDialogOpen && editJobRole === 'Manager') {
            axios.get('/api/sites', { withCredentials: true, baseURL: API_CONFIG.BASE_URL })
                .then(res => setAllSites(res.data || []))
                .catch(err => {
                    if (err?.response?.status === 401) {
                        try { sessionStorage.setItem('sessionExpired', 'true'); } catch {}
                        navigate('/login');
                    } else {
                        setAllSites([]);
                    }
                });
        }
    }, [editDialogOpen, editJobRole, navigate]);

    const getJobRoleColor = (role: string) => {
        switch (role) {
            case 'Admin': return '#d32f2f';
            case 'Manager': return '#115740';
            case 'Driver': return '#1976d2';
            default: return '#666';
        }
    };

    const getUserInitials = (name: string) => {
        return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
    };

    const handleEditUser = (user: User) => {
        setEditingUser(user);
        setEditName(user.name);
        setEditEmail(user.email);
        setEditPhone(user.phone_number);
        setEditJobRole(user.job_role);
        setEditSelectedSites(user.site_ids || []);
        setEditIsActive(user.is_active);
        setError('');
        setSuccess('');
        setEditDialogOpen(true);
    };

    const handleSiteChange = (siteId: string) => {
        if (editSelectedSites.includes(siteId)) {
            setEditSelectedSites(editSelectedSites.filter(id => id !== siteId));
        } else {
            setEditSelectedSites([...editSelectedSites, siteId]);
        }
    };

    const handleSubmitEdit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingUser) return;

        if (!editName || !editEmail || !editPhone || !editJobRole) {
            setError('Please fill in all fields.');
            return;
        }

        if (editJobRole === 'Manager' && editSelectedSites.length === 0) {
            setError('Please select at least one site for the manager.');
            return;
        }

        setError('');
        setSuccess('');

        try {
            await axios.put(`/api/users/${editingUser.user_id}`, {
                name: editName,
                email: editEmail,
                phone_number: editPhone,
                job_role: editJobRole,
                site_ids: editJobRole === 'Manager' ? editSelectedSites : undefined,
                is_active: editIsActive
            }, { withCredentials: true, baseURL: API_CONFIG.BASE_URL });

            setSuccess('User updated successfully');
            
            // Refresh users list
            const res = await axios.get('/api/all-users', { withCredentials: true, baseURL: API_CONFIG.BASE_URL });
            setUsers(res.data);
            
            // Close dialog after a short delay
            setTimeout(() => {
                setEditDialogOpen(false);
                setEditingUser(null);
            }, 1500);

        } catch (err: any) {
            if (err?.response?.status === 401) {
                try { sessionStorage.setItem('sessionExpired', 'true'); } catch {}
                navigate('/login');
                return;
            }
            setError(err.response?.data?.error || 'User update failed');
        }
    };

    const handleCloseEdit = () => {
        setEditDialogOpen(false);
        setEditingUser(null);
        setError('');
        setSuccess('');
    };

    if (checkingSession) return <div />;

    return (
        <div
            style={{
                width: '100%',
                minHeight: '100vh',
                background: '#f5f6fa',
                display: 'flex',
                flexDirection: 'column',
                padding: '40px',
                boxSizing: 'border-box'
            }}
            className="users-main-container"
        >
            <div className="users-header">
                <div className="users-title">
                    <h2 style={{
                        margin: 0,
                        fontSize: '2.4em',
                        fontWeight: 700,
                        letterSpacing: '0.5px',
                        color: '#333'
                    }}>
                        System Users
                    </h2>
                </div>
                <div className="create-button-container">
                    <button
                        style={{
                            background: '#115740',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            padding: '10px 22px',
                            fontWeight: 600,
                            fontSize: '1em',
                            cursor: 'pointer',
                            boxShadow: '0 2px 8px rgba(17,87,64,0.3)'
                        }}
                        onClick={() => navigate('/create-user')}
                    >
                        + Add User
                    </button>
                </div>
            </div>
            <div className="users-grid">
                {users.length === 0 ? (
                    <div style={{
                        color: '#666',
                        textAlign: 'center',
                        padding: 32,
                        width: '100%',
                        gridColumn: '1/-1',
                        background: '#fff',
                        borderRadius: 12,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                    }}>
                        No users found.
                    </div>
                ) : (
                    Array.from({ length: Math.max(users.length, 4) }).map((_, idx) => {
                        const user = users[idx];
                        if (!user) {
                            return <div key={`empty-${idx}`} style={{ width: CARD_WIDTH, height: CARD_HEIGHT, visibility: 'hidden' }} />;
                        }
                        return (
                            <div
                                key={user.user_id}
                                className="user-card"
                                style={{
                                    background: '#fff',
                                    borderRadius: 12,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                                    padding: 28,
                                    width: CARD_WIDTH,
                                    height: CARD_HEIGHT,
                                    position: 'relative',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    textAlign: 'center',
                                    overflow: 'hidden',
                                    wordBreak: 'break-word'
                                }}
                            >
                                {/* Circle initials */}
                                <div
                                    className="card-avatar"
                                    style={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: '50%',
                                        background: getJobRoleColor(user.job_role),
                                        color: '#fff',
                                        fontWeight: 700,
                                        fontSize: '1.2em',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        position: 'absolute',
                                        top: 22,
                                        left: 22
                                    }}
                                >
                                    {getUserInitials(user.name)}
                                </div>
                                {/* Edit icon */}
                                <div style={{
                                    position: 'absolute',
                                    top: 22,
                                    right: 22,
                                    display: 'flex',
                                    gap: 16
                                }}>
                                    <FaEdit
                                        className="card-edit-icon"
                                        style={{ color: '#888', cursor: 'pointer', fontSize: '1.1em' }}
                                        title="Edit User"
                                        onClick={() => handleEditUser(user)}
                                    />
                                </div>
                                <div style={{
                                    width: '90%',
                                    margin: '0 auto',
                                    position: 'relative',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    flex: 1,
                                    justifyContent: 'center'
                                }}>
                                    {/* Name */}
                                    <div
                                        className="card-title"
                                        style={{
                                            fontWeight: 700,
                                            fontSize: '1.1em',
                                            color: '#115740',
                                            marginTop: 54,
                                            marginBottom: 10,
                                            wordBreak: 'break-word',
                                            whiteSpace: 'normal',
                                            overflow: 'visible',
                                            textOverflow: 'unset'
                                        }}
                                    >
                                        {user.name}
                                    </div>
                                    {/* Job Role */}
                                    <div
                                        className="card-jobrole"
                                        style={{
                                            background: getJobRoleColor(user.job_role),
                                            color: '#fff',
                                            padding: '4px 12px',
                                            borderRadius: 16,
                                            fontSize: '0.85em',
                                            fontWeight: 600,
                                            marginBottom: 12
                                        }}
                                    >
                                        {user.job_role}
                                    </div>
                                    {/* Email */}
                                    <div
                                        className="card-email"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 8,
                                            color: '#444',
                                            fontSize: '0.85em',
                                            marginBottom: 8,
                                            width: '100%',
                                        }}
                                    >
                                        <FaEnvelope
                                            style={{
                                                color: '#115740',
                                                fontSize: '1em',
                                                flexShrink: 0
                                            }}
                                        />
                                        <span style={{
                                            wordBreak: 'break-word',
                                            whiteSpace: 'normal'
                                        }}>
                                            {user.email}
                                        </span>
                                    </div>
                                    {/* Phone */}
                                    <div
                                        className="card-phone"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 8,
                                            color: '#444',
                                            fontSize: '0.85em',
                                            marginBottom: 12,
                                            width: '100%',
                                        }}
                                    >
                                        <FaPhone
                                            style={{
                                                color: '#115740',
                                                fontSize: '1em',
                                                flexShrink: 0
                                            }}
                                        />
                                        <span>{user.phone_number}</span>
                                    </div>
                                    {/* Site Access for Managers */}
                                    {user.job_role === 'Manager' && user.site_ids && user.site_ids.length > 0 && (
                                        <div style={{
                                            width: '100%',
                                            marginTop: 8
                                        }}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 6,
                                                color: '#115740',
                                                fontSize: '0.9em',
                                                fontWeight: 600,
                                                marginBottom: 6
                                            }}>
                                                <FaMapMarkerAlt />
                                                <span>Site Access:</span>
                                            </div>
                                            <div style={{
                                                color: '#444',
                                                fontSize: '0.8em',
                                                lineHeight: 1.3,
                                                textAlign: 'center'
                                            }}>
                                                {user.site_ids.join(', ')}
                                            </div>
                                        </div>
                                    )}
                                    {/* Status indicator */}
                                    <div style={{
                                        position: 'absolute',
                                        bottom: -10,
                                        right: 0,
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        background: user.is_active ? '#4caf50' : '#f44336',
                                        border: '2px solid #fff',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                    }}
                                    title={user.is_active ? 'Active' : 'Inactive'}
                                    />
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
            {/* Edit User Dialog */}
            {editDialogOpen && editingUser && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, width: '100vw', height: '100vh',
                    background: 'rgba(0,0,0,0.18)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div style={{
                        background: '#fff', padding: '32px', borderRadius: '12px',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.12)', minWidth: 320
                    }}>
                        <h3 style={{ marginBottom: 18 }}>Edit User</h3>
                        <form onSubmit={handleSubmitEdit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <input
                                type="text"
                                placeholder="Full Name"
                                value={editName}
                                onChange={e => setEditName(e.target.value)}
                                style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
                                required
                            />
                            <input
                                type="email"
                                placeholder="Email Address"
                                value={editEmail}
                                onChange={e => setEditEmail(e.target.value)}
                                style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
                                required
                            />
                            <input
                                type="text"
                                placeholder="Phone Number"
                                value={editPhone}
                                onChange={e => setEditPhone(e.target.value)}
                                style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
                                required
                            />
                            <select
                                value={editJobRole}
                                onChange={e => setEditJobRole(e.target.value)}
                                style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
                                required
                            >
                                <option value="" disabled>Select Job Role</option>
                                <option value="Admin">Admin</option>
                                <option value="Manager">Manager</option>
                                <option value="Driver">Driver</option>
                            </select>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input
                                    type="checkbox"
                                    checked={editIsActive}
                                    onChange={e => setEditIsActive(e.target.checked)}
                                />
                                Active
                            </label>
                            {editJobRole === 'Manager' && (
                                <div style={{
                                    border: '1px solid #ccc',
                                    borderRadius: 6,
                                    padding: 12,
                                    maxHeight: 200,
                                    overflowY: 'auto'
                                }}>
                                    <div style={{ fontWeight: 600, marginBottom: 8 }}>Assign Sites:</div>
                                    {allSites.length === 0 ? (
                                        <div style={{ color: '#666', fontStyle: 'italic' }}>
                                            No sites available
                                        </div>
                                    ) : (
                                        allSites.map(site => (
                                            <label
                                                key={site.site_id}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 8,
                                                    padding: '4px 0',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    value={site.site_id}
                                                    checked={editSelectedSites.includes(site.site_id)}
                                                    onChange={() => handleSiteChange(site.site_id)}
                                                />
                                                <span style={{ fontWeight: 600 }}>{site.site_id}</span>
                                                <span>- {site.site_name}</span>
                                            </label>
                                        ))
                                    )}
                                </div>
                            )}
                            <div style={{ display: 'flex', gap: 12 }}>
                                <button
                                    type="submit"
                                    style={{
                                        padding: '10px 22px',
                                        borderRadius: 6,
                                        background: '#115740',
                                        color: '#fff',
                                        border: 'none',
                                        fontWeight: 600
                                    }}
                                >
                                    Update User
                                </button>
                                <button
                                    type="button"
                                    style={{
                                        padding: '10px 22px',
                                        borderRadius: 6,
                                        background: '#eee',
                                        color: '#333',
                                        border: 'none',
                                        fontWeight: 500
                                    }}
                                    onClick={handleCloseEdit}
                                >
                                    Cancel
                                </button>
                            </div>
                            {success && <div style={{ color: '#115740', fontWeight: 500 }}>{success}</div>}
                            {error && <div style={{ color: '#d32f2f', fontWeight: 500 }}>{error}</div>}
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Users;